import LegalLayout from "../../components/legal/LegalLayout";
import { useSettings } from "../../features/settings/api";
import useSEO from "../../hooks/useSEO";

// Generic template, accurate to the actual order-request checkout model
// (no online payment, a human confirms every sale) — but the bracketed
// fields need the business's real legal name / registered address /
// governing jurisdiction filled in. Not legal advice; have it reviewed
// before relying on it in production.
export default function Terms() {
  useSEO({ title: "Terms of Service" });
  const { data: settings } = useSettings();
  const contactEmail = settings?.contactEmail || "[business contact email]";

  return (
    <LegalLayout title="Terms of Service" updated="12 August 2026">
      <p>
        These terms govern your use of the Smart Connect Electrical website and any order request
        you submit through it. By using this site, you agree to them.
      </p>

      <Section title="Order requests, not purchases">
        <p>
          This site does not process payments online. Adding items to your cart and submitting
          checkout creates an <strong>order request</strong> — it is not a binding sale. A member
          of our team will contact you (by WhatsApp or phone) to confirm item availability, final
          pricing, and payment/delivery arrangements before any sale is final. Prices, discounts,
          and stock shown on the site are estimates at the time of browsing and may change before
          your order is confirmed.
        </p>
      </Section>

      <Section title="Accounts">
        <p>
          Creating an account is optional — you can submit an order request as a guest. If you do
          create one, you're responsible for keeping your password secure and for activity under
          your account. Let us know immediately if you suspect unauthorized access.
        </p>
      </Section>

      <Section title="Product information">
        <p>
          We try to keep product descriptions, specifications, and images accurate, but we don't
          guarantee they're error-free, complete, or current at every moment. If something's wrong
          with an order because of a listing error, we'll work with you to fix it when we confirm
          the order.
        </p>
      </Section>

      <Section title="Reviews">
        <p>
          By submitting a review, you confirm it reflects your own genuine opinion and grant us the
          right to display it on this site. Reviews are moderated before publication and we may
          decline to publish or may remove any review at our discretion (e.g. spam, abuse, or
          content unrelated to the product).
        </p>
      </Section>

      <Section title="Coupons and pricing">
        <p>
          Coupon codes are subject to the terms shown when applied and may be changed or withdrawn
          at any time. Discounts don't stack unless explicitly stated.
        </p>
      </Section>

      <Section title="Delivery and fulfillment">
        <p>
          Delivery timelines and methods are arranged directly with you once your order is
          confirmed, and aren't guaranteed by this website.
        </p>
      </Section>

      <Section title="Returns and cancellations">
        <p>
          Because every order is confirmed individually rather than processed automatically,
          returns, exchanges, and cancellations are handled case by case — contact us at{" "}
          <a className="text-gold-600 underline dark:text-gold-400" href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
          and we'll work it out with you.
        </p>
      </Section>

      <Section title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, Smart Connect Electrical isn't liable for
          indirect, incidental, or consequential damages arising from your use of this site or an
          order placed through it.
        </p>
      </Section>

      <Section title="Changes to these terms">
        <p>We may update these terms from time to time; the date above reflects the latest revision.</p>
      </Section>

      <Section title="Governing law">
        <p>
          These terms are governed by the laws of{" "}
          <span className="rounded bg-mono/[0.06] px-1 py-0.5 font-mono text-xs">
            [Governing jurisdiction — e.g. state/country of business registration]
          </span>, without regard to conflict-of-law principles.
        </p>
      </Section>

      <p className="text-xs text-mono/40">
        This is a template, not legal advice. Confirm it matches your actual business practices
        and applicable consumer-protection law before relying on it, and fill in the business's
        registered legal name, address, and jurisdiction where marked.
      </p>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="mb-1.5 font-serif text-base text-ink">{title}</h2>
      {children}
    </section>
  );
}
